import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Settings, 
  Zap, 
  Share2, 
  Save, 
  AlertCircle, 
  CheckCircle,
  Eye,
  EyeOff,
  RefreshCw,
  DollarSign,
  X,
  Edit3,
  Image,
  Instagram,
  Facebook,
  Video,
  Twitter,
  MessageCircle,
  MessageSquare,
  Mail,
  Megaphone,
  FileText,
  Trash2,
  Plus,
  Calendar,
  Edit2,
  Search,
  Gift,
  Heart,
  Stethoscope,
  Sparkles
} from 'lucide-react';

// Placeholder Modal Component (reused from EmailConfiguration)
const PlaceholderModal = ({ isOpen, onClose, onInsertPlaceholder }) => {
  const [globalPlaceholders, setGlobalPlaceholders] = React.useState([]);
  const [businessPlaceholders, setBusinessPlaceholders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Fetch placeholders when modal opens
  React.useEffect(() => {
    if (isOpen) {
      fetchPlaceholders();
    }
  }, [isOpen]);

  const fetchPlaceholders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Fetch global placeholders
      const globalResponse = await fetch(`${API_BASE_URL}/api/global-placeholders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch business placeholders
      const businessResponse = await fetch(`${API_BASE_URL}/api/business-placeholders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (globalResponse.ok) {
        const globalData = await globalResponse.json();
        setGlobalPlaceholders(globalData);
      }

      if (businessResponse.ok) {
        const businessData = await businessResponse.json();
        setBusinessPlaceholders(businessData);
      }
    } catch (error) {
      console.error('Error fetching placeholders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Combine and categorize placeholders
  const getPlaceholders = () => {
    const allPlaceholders = [];

    // Add global placeholders
    globalPlaceholders.forEach(placeholder => {
      allPlaceholders.push({
        code: placeholder.placeholder,
        category: 'Global',
        value: placeholder.value
      });
    });

    // Add business placeholders
    businessPlaceholders.forEach(placeholder => {
      allPlaceholders.push({
        code: placeholder.placeholder,
        category: 'Custom',
        value: placeholder.value
      });
    });

    return allPlaceholders;
  };

  const placeholders = getPlaceholders();
  const categories = [...new Set(placeholders.map(p => p.category))];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Sticky Header */}
        <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Available Placeholders</h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-white opacity-90 mt-1">
            Click "Insert" to add a placeholder into your template
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--theme-primary)' }}></div>
              <p className="text-gray-600">Loading placeholders...</p>
            </div>
          ) : placeholders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No placeholders available. Create some in Business Info or CMS Settings.</p>
            </div>
          ) : (
            categories.map(category => (
              <div key={category} className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 border-b pb-1">{category}</h4>
                <div className="space-y-2">
                  {placeholders
                    .filter(placeholder => placeholder.category === category)
                    .map(placeholder => (
                      <div key={placeholder.code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                        <div className="flex-1 space-y-1">
                          <div>
                            <code className="text-sm font-mono bg-white px-2 py-1 rounded border" style={{ color: 'var(--theme-primary)' }}>
                              {placeholder.code}
                            </code>
                          </div>
                          {placeholder.value && (
                            <div className="text-xs text-gray-600">
                              {placeholder.value}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => onInsertPlaceholder(placeholder.code)}
                          className="ml-3 px-3 py-1 text-white text-sm rounded transition-colors"
                          style={{ backgroundColor: 'var(--theme-primary)' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                        >
                          Insert
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Sticky Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AISettings = () => {
  const { user, token, isAdmin, loading: authLoading, API_BASE_URL } = useAuth();
  const [activeTab, setActiveTab] = useState('openai');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pageLoading, setPageLoading] = useState(true);

  // Prompts State - now organized by business type
  const [prompts, setPrompts] = useState({
    current_business_type: 'veterinary_hospital',
    veterinary_hospital: { openai_content_processing: '', claude_html_generation: '' },
    restaurant: { openai_content_processing: '', claude_html_generation: '' },
    grocery_store: { openai_content_processing: '', claude_html_generation: '' },
    restaurant_grocery_store: { openai_content_processing: '', claude_html_generation: '' }
  });
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [selectedBusinessType, setSelectedBusinessType] = useState('veterinary_hospital');

  // OpenRouter Settings
  const [openRouterSettings, setOpenRouterSettings] = useState({
    api_key: '',
    provisioning_key: '',
    app_name: 'Veterinary AI Marketing System',
    enabled: true,
    chat_model: 'openai/gpt-5',
    image_model: 'google/gemini-2.5-flash-image-preview',
    social_media_model: 'openai/gpt-5',
    sms_model: 'openai/gpt-5-mini',
    email_model: 'openai/gpt-5-mini',
    marketing_model: 'openai/gpt-5',
    credits_balance: 0,
    low_balance_threshold: 5.0,
    auto_topup_enabled: false,
    auto_topup_amount: 20.0,
    monthly_limit: null,
    connection_status: 'unknown'
  });

  const [availableModels, setAvailableModels] = useState([]);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [showCleanUsageModal, setShowCleanUsageModal] = useState(false);
  const [cleaningUsageData, setCleaningUsageData] = useState(false);
  const [showProvisioningKey, setShowProvisioningKey] = useState(false);
  const [showSavingModal, setShowSavingModal] = useState(false);

  // Social Media Settings
  const [socialSettings, setSocialSettings] = useState({
    facebook: {
      app_id: '',
      app_secret: '',
      access_token: '',
      page_id: ''
    },
    instagram: {
      access_token: '',
      user_id: ''
    },
    twitter: {
      api_key: '',
      api_secret: '',
      access_token: '',
      access_token_secret: ''
    },
    tiktok: {
      client_key: '',
      client_secret: '',
      access_token: ''
    },
    whatsapp: {
      business_account_id: '',
      access_token: '',
      phone_number_id: ''
    }
  });

  const [showApiKeys, setShowApiKeys] = useState({
    openai_api_key: false,
    anthropic_api_key: false,
    google_api_key: false,
    openrouter_api_key: false,
    facebook_access_token: false,
    instagram_access_token: false,
    twitter_access_token: false,
    tiktok_access_token: false,
    whatsapp_access_token: false,
    aws_access_key: false,
    aws_secret_key: false
  });

  // Signature Templates state
  const [signatureTemplates, setSignatureTemplates] = useState({
    aiEmailSignature: 'Best regards,\n[BUSINESS_NAME]\n[BUSINESS_ADDRESS]\n[PHONE_NUMBER]\n[BOOK_NOW_LINK]\n[WEBSITE_LINK]\n----',
    aiPhotoSignature: '[BUSINESS_NAME] • Call [PHONE_NUMBER] for expert veterinary care',
    aiSmsSignature: '[SMS_SIGNATURE]',
    aiEmailSubjectTemplate: '[BUSINESS_SHORT_NAME]: [CHATGPT_CONTENT_TITLE]'
  });
  
  const [signatureTemplatesLoading, setSignatureTemplatesLoading] = useState(false);
  
  // Modal states for individual template editing
  const [editingTemplate, setEditingTemplate] = useState(null); // 'email', 'photo', 'sms', 'subject'
  const [tempTemplateContent, setTempTemplateContent] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const templateTextareaRef = useRef(null);
  
  // Placeholder Modal State
  const [placeholderModalOpen, setPlaceholderModalOpen] = useState(false);

  // Image Formats state
  const [socialMediaImageSettings, setSocialMediaImageSettings] = useState({
    instagram: {
      post: { width: 1080, height: 1080, include_signature: true, include_text: true, output_style: "square" },
      profile_picture: { width: 320, height: 320, include_signature: false, include_text: false, output_style: "square" },
      landscape: { width: 1080, height: 566, include_signature: true, include_text: true, output_style: "landscape" },
      square: { width: 1080, height: 1080, include_signature: true, include_text: true, output_style: "square" },
      story_reels: { width: 1080, height: 1920, include_signature: true, include_text: true, output_style: "portrait" },
      cover_photo: { width: 820, height: 312, include_signature: false, include_text: false, output_style: "landscape" }
    },
    facebook: {
      post: { width: 1080, height: 1080, include_signature: true, include_text: true, output_style: "square" },
      profile_picture: { width: 500, height: 500, include_signature: false, include_text: false, output_style: "square" },
      landscape: { width: 1080, height: 566, include_signature: true, include_text: true, output_style: "landscape" },
      square: { width: 1080, height: 1080, include_signature: true, include_text: true, output_style: "square" },
      story_reels: { width: 1080, height: 1920, include_signature: true, include_text: true, output_style: "portrait" },
      cover_photo: { width: 851, height: 315, include_signature: false, include_text: false, output_style: "landscape" }
    },
    tiktok: {
      post: { width: 1080, height: 1920, include_signature: true, include_text: true, output_style: "portrait" },
      profile_picture: { width: 20, height: 20, include_signature: false, include_text: false, output_style: "square" },
      landscape: { width: 1920, height: 1080, include_signature: true, include_text: true, output_style: "landscape" },
      square: { width: 640, height: 640, include_signature: true, include_text: true, output_style: "square" },
      story_reels: { width: 1080, height: 1920, include_signature: true, include_text: true, output_style: "portrait" }
    },
    x: {
      post: { width: 1080, height: 1080, include_signature: true, include_text: true, output_style: "square" },
      profile_picture: { width: 400, height: 400, include_signature: false, include_text: false, output_style: "square" },
      landscape: { width: 1280, height: 720, include_signature: true, include_text: true, output_style: "landscape" },
      square: { width: 720, height: 720, include_signature: true, include_text: true, output_style: "square" },
      cover_photo: { width: 1500, height: 500, include_signature: false, include_text: false, output_style: "landscape" }
    },
    whatsapp: {
      post: { width: 1080, height: 1080, include_signature: true, include_text: true, output_style: "square" }
    },
    sms: {
      portrait: { width: 1080, height: 1920, include_signature: true, include_text: true, output_style: "portrait" },
      landscape: { width: 1280, height: 720, include_signature: true, include_text: true, output_style: "landscape" },
      square: { width: 600, height: 600, include_signature: true, include_text: true, output_style: "square" }
    },
    email: {
      portrait: { width: 1080, height: 1920, include_signature: true, include_text: true, output_style: "portrait" },
      landscape: { width: 1280, height: 720, include_signature: true, include_text: true, output_style: "landscape" },
      square: { width: 600, height: 600, include_signature: true, include_text: true, output_style: "square" }
    },
    popup: {
      portrait: { 
        width: 360, 
        height: 540, 
        include_signature: false, 
        include_text: false, 
        output_style: "portrait",
        dalle_aspect_ratio: "1024x1536",
        gpt_image_aspect_ratio: "1024x1536",
        gemini_aspect_ratio: "9:16"
      },
      landscape: { 
        width: 1200, 
        height: 600, 
        include_signature: false, 
        include_text: false, 
        output_style: "landscape",
        dalle_aspect_ratio: "1792x1024",
        gpt_image_aspect_ratio: "1536x1024",
        gemini_aspect_ratio: "16:9"
      },
      square: { 
        width: 400, 
        height: 400, 
        include_signature: false, 
        include_text: false, 
        output_style: "square",
        dalle_aspect_ratio: "1024x1024",
        gpt_image_aspect_ratio: "1024x1024",
        gemini_aspect_ratio: "1:1"
      }
    },
    marketing: {
      poster: { width: 1080, height: 1920, include_signature: true, include_text: true, output_style: "portrait" },
      flyer: { width: 816, height: 1056, include_signature: true, include_text: true, output_style: "portrait" },
      square: { width: 1080, height: 1080, include_signature: true, include_text: true, output_style: "square" }
    }
  });
  const [socialMediaImageSettingsLoading, setSocialMediaImageSettingsLoading] = useState(false);
  const [formatSavingStates, setFormatSavingStates] = useState({}); // Track loading state for each format
  const [saveModal, setSaveModal] = useState({ show: false, platform: '', format: '', status: '', message: '' });
  const [platformFilter, setPlatformFilter] = useState('all'); // Filter state for image formats
  
  // Create/Delete Format Modal States
  const [showCreateFormatModal, setShowCreateFormatModal] = useState(false);
  const [createFormatPlatform, setCreateFormatPlatform] = useState('');
  const [newFormatKey, setNewFormatKey] = useState('');
  const [newFormatName, setNewFormatName] = useState('');
  const [newFormatWidth, setNewFormatWidth] = useState(1080);
  const [newFormatHeight, setNewFormatHeight] = useState(1080);
  const [newFormatIncludeSignature, setNewFormatIncludeSignature] = useState(true);
  const [newFormatIncludeText, setNewFormatIncludeText] = useState(true);
  const [newFormatDalleAspect, setNewFormatDalleAspect] = useState('1024x1024');
  const [newFormatGptImageAspect, setNewFormatGptImageAspect] = useState('1024x1024');
  const [newFormatGeminiAspect, setNewFormatGeminiAspect] = useState('1:1');
  const [creatingFormat, setCreatingFormat] = useState(false);
  const [deletingFormat, setDeletingFormat] = useState({});
  
  // Delete Confirmation Modal State
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteFormatData, setDeleteFormatData] = useState({ platform: '', format: '', formatName: '' });
  
  // Reference Images state
  const [referenceImages, setReferenceImages] = useState({});
  const [referenceImagesLoading, setReferenceImagesLoading] = useState(false);
  const [generatingReferenceImages, setGeneratingReferenceImages] = useState(false);
  const [referenceImageStatus, setReferenceImageStatus] = useState({});

  // OpenAI Settings state
  const [openaiSettings, setOpenaiSettings] = useState({
    openai_api_key: '',
    google_api_key: '',
    anthropic_api_key: '',
    text_generation_model: 'gpt-4o',
    image_generation_model: 'dall-e-3',
    image_quality: 'hd',
    claude_model: 'claude-sonnet-4-20250514',
    use_openai_for_all_services: false,  // New master override
    enable_ai_for_pdf_tools: false,  // New: Enable AI for PDF page number detection
    // Deprecated but kept for backward compatibility
    override_openrouter_text: false,
    override_openrouter_image: false
  });
  const [openaiSettingsLoading, setOpenaiSettingsLoading] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showGoogleKey, setShowGoogleKey] = useState(false); // For API key visibility toggle
  const [showAnthropicKey, setShowAnthropicKey] = useState(false); // For Anthropic API key visibility
  const [openaiTextModels, setOpenaiTextModels] = useState([
    {value: 'gpt-4o', label: 'GPT-4o (Latest)'},
    {value: 'gpt-4o-mini', label: 'GPT-4o Mini'},
    {value: 'gpt-4-turbo', label: 'GPT-4 Turbo'},
    {value: 'gpt-4', label: 'GPT-4'},
    {value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo'},
    {value: 'chatgpt-4o-latest', label: 'ChatGPT-4o Latest'}
  ]);
  const [claudeModels, setClaudeModels] = useState([
    {value: 'claude-sonnet-4-5-20250514', label: 'Claude Sonnet 4.5 (Latest)'},
    {value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4'},
    {value: 'claude-opus-4-20250514', label: 'Claude Opus 4 (Latest)'},
    {value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet'},
    {value: 'claude-3-opus-20240229', label: 'Claude 3 Opus'}
  ]);
  const [openaiImageModels, setOpenaiImageModels] = useState([
    {value: 'dall-e-3', label: 'DALL-E 3 (OpenAI)'},
    {value: 'dall-e-2', label: 'DALL-E 2 (OpenAI)'},
    {value: 'google/gemini-2.0-flash-exp-image-generation', label: 'Nano Banana (Gemini 2.0 Flash)'},
    {value: 'google/imagen-4.0-generate-001', label: 'Nano Banana Pro (Imagen 4)'}
  ]);
  const [openaiImageQualityOptions, setOpenaiImageQualityOptions] = useState([
    {value: 'hd', label: 'HD (High Quality)'},
    {value: 'standard', label: 'Standard (Faster & Cheaper)'}
  ]);
  
  const [gptImageQualityOptions, setGptImageQualityOptions] = useState([
    {value: 'high', label: 'High'},
    {value: 'medium', label: 'Medium'}, 
    {value: 'low', label: 'Low'},
    {value: 'auto', label: 'Auto'}
  ]);

  // Holiday Management States
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidaySearchTerm, setHolidaySearchTerm] = useState('');
  const [selectedHolidayCategory, setSelectedHolidayCategory] = useState('all');
  const [showAddHolidayModal, setShowAddHolidayModal] = useState(false);
  const [showEditHolidayModal, setShowEditHolidayModal] = useState(false);
  const [showDeleteHolidayModal, setShowDeleteHolidayModal] = useState(false);
  const [showRefreshHolidayModal, setShowRefreshHolidayModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [holidayFormData, setHolidayFormData] = useState({
    name: '',
    date: '',
    month_day: '',
    is_recurring: true,
    is_enabled: true,
    category: 'general'
  });

  const holidayCategories = [
    { value: 'all', label: 'All Categories', icon: Calendar },
    { value: 'general', label: 'General', icon: Calendar },
    { value: 'pet', label: 'Pet Days', icon: Heart },
    { value: 'veterinary', label: 'Veterinary', icon: Stethoscope },
    { value: 'family', label: 'Family', icon: Gift }
  ];

  const tabs = [
    { 
      id: 'openai', 
      label: 'API Keys', 
      icon: Settings, 
      color: '#10b981',
      description: 'API Keys and AI model configuration'
    },
    { 
      id: 'prompts', 
      label: 'Prompts', 
      icon: FileText, 
      color: '#3b82f6',
      description: 'AI prompts for content generation'
    },
    { 
      id: 'social', 
      label: 'Social Media', 
      icon: Share2, 
      color: '#dc2626',
      description: 'Social media platform integrations'
    },
    { 
      id: 'signature-templates', 
      label: 'Signature Templates', 
      icon: Settings, 
      color: '#059669',
      description: 'Configure AI-generated content signatures and branding'
    },
    { 
      id: 'social-media-image-settings', 
      label: 'Image Formats', 
      icon: Image, 
      color: '#0891b2',
      description: 'Configure social media platform image dimensions and formats'
    },
    { 
      id: 'manage-holidays', 
      label: 'Manage Holidays', 
      icon: Calendar, 
      color: '#f59e0b',
      description: 'Manage holidays for AI agents and automation'
    },
    { 
      id: 'openrouter', 
      label: 'OpenRouter', 
      icon: Zap, 
      color: '#8b5cf6',
      description: 'OpenRouter AI models and configuration'
    }
  ];

  // Auth check - restrict to Owner and Admin roles only
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = '/login';
    } else if (!authLoading && user) {
      if (user.role === 'owner' || user.role === 'admin') {
        setPageLoading(false);
        fetchSettings();
      } else {
        // For non-owner/admin users, just finish loading - access control handled in render
        setPageLoading(false);
      }
    }
  }, [user, authLoading]);

  // Fetch holidays when tab is active
  useEffect(() => {
    if (activeTab === 'manage-holidays' && holidays.length === 0 && user && token) {
      fetchHolidays();
    }
  }, [activeTab, holidays.length, user, token]);

  const fetchSettings = async () => {
    try {
      // Load general settings
      const response = await fetch(`${API_BASE_URL}/api/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSocialSettings(prev => ({ ...prev, ...data.social_settings }));
      }

      // Load OpenRouter settings from AI settings endpoint
      const aiResponse = await fetch(`${API_BASE_URL}/api/ai/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (aiResponse.ok) {
        const aiData = await aiResponse.json();
        if (aiData.success && aiData.settings) {
          setOpenRouterSettings(prev => ({
            ...prev,
            api_key: aiData.settings.api_key || '',
            provisioning_key: aiData.settings.provisioning_key || '',
            app_name: aiData.settings.app_name || 'Veterinary AI Marketing System',
            enabled: aiData.settings.enabled !== undefined ? aiData.settings.enabled : true,
            chat_model: aiData.settings.chat_model || 'openai/gpt-5',
            image_model: aiData.settings.image_model || 'google/gemini-2.5-flash-image-preview',
            social_media_model: aiData.settings.social_media_model || 'openai/gpt-5',
            sms_model: aiData.settings.sms_model || 'openai/gpt-5-mini',
            email_model: aiData.settings.email_model || 'openai/gpt-5-mini',
            marketing_model: aiData.settings.marketing_model || 'openai/gpt-5',
            connection_status: aiData.settings.connection_status || 'unknown',
            credits_balance: aiData.settings.credits_balance || 0,
            low_balance_threshold: aiData.settings.low_balance_threshold || 5.0,
            auto_topup_enabled: aiData.settings.auto_topup_enabled || false,
            auto_topup_amount: aiData.settings.auto_topup_amount || 20.0,
            monthly_limit: aiData.settings.monthly_limit || null
          }));
          
          // Load available models if OpenRouter is configured
          if (aiData.settings.api_key) {
            await loadAvailableModels();
          }
        }
      }

      // Load signature templates
      await loadSignatureTemplates();

      // Load image formats
      await loadSocialMediaImageSettings();

      // Load reference images
      await loadReferenceImages();

      // Load reference image status
      await loadReferenceImageStatus();

      // Load OpenAI settings
      await loadOpenAISettings();

      // Load prompts
      await loadPrompts();
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const loadPrompts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/prompts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPrompts({
          current_business_type: data.current_business_type || 'veterinary_hospital',
          veterinary_hospital: {
            openai_content_processing: data.veterinary_hospital?.openai_content_processing || '',
            claude_html_generation: data.veterinary_hospital?.claude_html_generation || ''
          },
          restaurant: {
            openai_content_processing: data.restaurant?.openai_content_processing || '',
            claude_html_generation: data.restaurant?.claude_html_generation || ''
          },
          grocery_store: {
            openai_content_processing: data.grocery_store?.openai_content_processing || '',
            claude_html_generation: data.grocery_store?.claude_html_generation || ''
          },
          restaurant_grocery_store: {
            openai_content_processing: data.restaurant_grocery_store?.openai_content_processing || '',
            claude_html_generation: data.restaurant_grocery_store?.claude_html_generation || ''
          }
        });
        setSelectedBusinessType(data.current_business_type || 'veterinary_hospital');
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  };

  const savePrompts = async () => {
    setSavingPrompts(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/prompts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          veterinary_hospital: prompts.veterinary_hospital,
          restaurant: prompts.restaurant,
          grocery_store: prompts.grocery_store,
          restaurant_grocery_store: prompts.restaurant_grocery_store
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Prompts saved successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Failed to save prompts');
      }
    } catch (error) {
      console.error('Error saving prompts:', error);
      setMessage({ type: 'error', text: 'Failed to save prompts' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSavingPrompts(false);
    }
  };

  const loadSignatureTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-signature-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSignatureTemplates({
          aiEmailSignature: data.aiEmailSignature || 'Best regards,\n[BUSINESS_NAME]\n[BUSINESS_ADDRESS]\n[PHONE_NUMBER]\n[BOOK_NOW_LINK]\n[WEBSITE_LINK]\n----',
          aiPhotoSignature: data.aiPhotoSignature || '[BUSINESS_NAME] • Call [PHONE_NUMBER] for expert veterinary care',
          aiSmsSignature: data.aiSmsSignature || '[SMS_SIGNATURE]',
          aiEmailSubjectTemplate: data.aiEmailSubjectTemplate || '[BUSINESS_SHORT_NAME]: [CHATGPT_CONTENT_TITLE]'
        });
      }
    } catch (error) {
      console.error('Error loading signature templates:', error);
      // Keep default values if loading fails
    }
  };

  const loadSocialMediaImageSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/social-media-image-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // The GET endpoint returns platforms data directly
        if (data && typeof data === 'object') {
          // Start with initial state (all platforms with defaults) and merge backend data
          const transformedSettings = { ...socialMediaImageSettings };
          
          Object.keys(data).forEach(platformKey => {
            const platform = data[platformKey];
            if (!transformedSettings[platformKey]) {
              transformedSettings[platformKey] = {};
            }
            if (platform.formats) {
              Object.keys(platform.formats).forEach(formatKey => {
                const format = platform.formats[formatKey];
                transformedSettings[platformKey][formatKey] = {
                  width: format.width,
                  height: format.height,
                  include_signature: format.include_signature,
                  include_text: format.include_text !== undefined ? format.include_text : true,
                  output_style: format.output_style || "square",
                  dalle_aspect_ratio: format.dalle_aspect_ratio || "1024x1024",
                  gpt_image_aspect_ratio: format.gpt_image_aspect_ratio || "1024x1024",
                  gemini_aspect_ratio: format.gemini_aspect_ratio || "1:1"
                };
                
                console.log(`📥 Loaded ${platformKey}.${formatKey}:`, {
                  dalle: format.dalle_aspect_ratio,
                  gpt: format.gpt_image_aspect_ratio,
                  gemini: format.gemini_aspect_ratio
                });
              });
            }
          });
          
          setSocialMediaImageSettings(transformedSettings);
        }
      }
    } catch (error) {
      console.error('Error loading image formats:', error);
      // Keep default values if loading fails
    }
  };

  const saveIndividualFormatSettings = async (platform, format) => {
    const formatKey = `${platform}-${format}`;
    const descriptions = {
      post: 'Post',
      profile_picture: 'Profile Picture', 
      landscape: 'Landscape',
      square: 'Square',
      story_reels: 'Story/Reels',
      cover_photo: 'Cover Photo'
    };

    try {
      // Set loading state for this specific format
      setFormatSavingStates(prev => ({ ...prev, [formatKey]: true }));
      
      // Show saving modal
      setSaveModal({
        show: true,
        platform,
        format,
        status: 'saving',
        message: `Saving ${descriptions[format] || format} settings...`
      });

      // Get current settings from database
      const response = await fetch(`${API_BASE_URL}/api/social-media-image-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load current settings');
      }

      const currentData = await response.json();
      
      console.log('Current data from server:', currentData);
      console.log('Current data structure:', Object.keys(currentData));
      
      // The GET endpoint returns platforms data directly
      // Always start with default structure to ensure all platforms exist
      let platforms = {
        instagram: {
          platform_name: 'instagram',
          display_name: 'Instagram',
          formats: {}
        },
        facebook: {
          platform_name: 'facebook', 
          display_name: 'Facebook',
          formats: {}
        },
        tiktok: {
          platform_name: 'tiktok',
          display_name: 'TikTok', 
          formats: {}
        },
        x: {
          platform_name: 'x',
          display_name: 'X (Twitter)',
          formats: {}
        },
        whatsapp: {
          platform_name: 'whatsapp',
          display_name: 'WhatsApp',
          formats: {}
        },
        popup: {
          platform_name: 'popup',
          display_name: 'Landing Page Popups',
          formats: {}
        },
        sms: {
          platform_name: 'sms',
          display_name: 'SMS',
          formats: {}
        },
        email: {
          platform_name: 'email',
          display_name: 'Email',
          formats: {}
        }
      };
      
      // Merge with existing data
      if (currentData && typeof currentData === 'object') {
        Object.keys(currentData).forEach(platformKey => {
          if (platforms[platformKey] && currentData[platformKey].formats) {
            platforms[platformKey].formats = { ...currentData[platformKey].formats };
          } else if (!platforms[platformKey]) {
            // If platform exists in backend but not in our default list, keep it
            platforms[platformKey] = currentData[platformKey];
          }
        });
      }

      // Ensure platform structure exists
      if (!platforms[platform]) {
        platforms[platform] = {
          platform_name: platform,
          display_name: platform.charAt(0).toUpperCase() + platform.slice(1),
          formats: {}
        };
      }
      if (!platforms[platform].formats) {
        platforms[platform].formats = {};
      }

      const formatData = socialMediaImageSettings[platform][format];
      const aspectRatio = formatData.width === formatData.height ? "1:1" : 
        formatData.width > formatData.height ? `${(formatData.width/formatData.height).toFixed(2)}:1` : 
        `${formatData.width}:${formatData.height}`;

      platforms[platform].formats[format] = {
        width: formatData.width,
        height: formatData.height,
        aspect_ratio: aspectRatio,
        description: descriptions[format] || format,
        include_signature: formatData.include_signature,
        include_text: formatData.include_text !== undefined ? formatData.include_text : true,
        output_style: formatData.output_style || "square",
        dalle_aspect_ratio: formatData.dalle_aspect_ratio || "1024x1024",
        gpt_image_aspect_ratio: formatData.gpt_image_aspect_ratio || "1024x1024",
        gemini_aspect_ratio: formatData.gemini_aspect_ratio || "1:1"
      };
      
      console.log('💾 Saving format data:', {
        platform,
        format,
        data: platforms[platform].formats[format]
      });

      const saveResponse = await fetch(`${API_BASE_URL}/api/social-media-image-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ platforms })
      });

      if (saveResponse.ok) {
        // Reload the settings to reflect the changes
        await loadSocialMediaImageSettings();
        
        // Show success modal
        setSaveModal({
          show: true,
          platform,
          format,
          status: 'success',
          message: `${descriptions[format] || format} settings saved successfully!`
        });

        // Auto-close modal after 2 seconds
        setTimeout(() => {
          setSaveModal({ show: false, platform: '', format: '', status: '', message: '' });
        }, 2000);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving individual format settings:', error);
      
      // Show error modal
      setSaveModal({
        show: true,
        platform,
        format,
        status: 'error',
        message: `Failed to save ${descriptions[format] || format} settings. Please try again.`
      });

      // Auto-close error modal after 4 seconds
      setTimeout(() => {
        setSaveModal({ show: false, platform: '', format: '', status: '', message: '' });
      }, 4000);
    } finally {
      // Clear loading state for this specific format
      setFormatSavingStates(prev => ({ ...prev, [formatKey]: false }));
    }
  };

  const saveSocialMediaImageSettings = async () => {
    setSocialMediaImageSettingsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Transform frontend state to backend structure
      const platforms = {};
      Object.keys(socialMediaImageSettings).forEach(platformKey => {
        const displayNames = {
          instagram: 'Instagram',
          facebook: 'Facebook', 
          tiktok: 'TikTok',
          x: 'X (Twitter)',
          whatsapp: 'WhatsApp',
          sms: 'SMS',
          email: 'Email',
          marketing: 'Promotion Material'
        };
        
        platforms[platformKey] = {
          platform_name: platformKey,
          display_name: displayNames[platformKey] || platformKey,
          formats: {}
        };

        Object.keys(socialMediaImageSettings[platformKey]).forEach(formatKey => {
          const format = socialMediaImageSettings[platformKey][formatKey];
          const aspectRatio = format.width === format.height ? "1:1" : 
            format.width > format.height ? `${(format.width/format.height).toFixed(2)}:1` : 
            `${format.width}:${format.height}`;
          
          const descriptions = {
            post: 'Post',
            profile_picture: 'Profile Picture',
            landscape: 'Landscape',
            square: 'Square',
            story_reels: 'Story/Reels',
            cover_photo: 'Cover Photo',
            portrait: 'Portrait',
            poster: 'Poster',
            flyer: 'Flyer'
          };

          platforms[platformKey].formats[formatKey] = {
            width: format.width,
            height: format.height,
            aspect_ratio: aspectRatio,
            description: descriptions[formatKey] || formatKey,
            include_signature: format.include_signature,
            include_text: format.include_text !== undefined ? format.include_text : true,
            output_style: format.output_style || "square",
            dalle_aspect_ratio: format.dalle_aspect_ratio || "1024x1024",
            gpt_image_aspect_ratio: format.gpt_image_aspect_ratio || "1024x1024",
            gemini_aspect_ratio: format.gemini_aspect_ratio || "1:1"
          };
        });
      });

      const response = await fetch(`${API_BASE_URL}/api/social-media-image-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ platforms })
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: 'Image Formats saved successfully!' 
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving image formats:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to save image formats. Please try again.' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setSocialMediaImageSettingsLoading(false);
    }
  };

  // Handle Delete Format
  // Handle Delete Format - Show Confirmation Modal
  const handleDeleteFormat = (platform, format, formatName = format) => {
    setDeleteFormatData({ platform, format, formatName });
    setShowDeleteConfirmModal(true);
  };

  // Confirm Delete Format
  const confirmDeleteFormat = async () => {
    const { platform, format } = deleteFormatData;
    
    try {
      setDeletingFormat(prev => ({ ...prev, [`${platform}-${format}`]: true }));
      setShowDeleteConfirmModal(false);
      
      // Remove from local state
      const newSettings = { ...socialMediaImageSettings };
      delete newSettings[platform][format];
      setSocialMediaImageSettings(newSettings);
      
      // Save to backend
      await saveSocialMediaImageSettings();
      
      setMessage({ 
        type: 'success', 
        text: `Format "${format}" deleted successfully from ${platform}!` 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting format:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to delete format. Please try again.' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      
      // Reload settings to restore state
      await loadSocialMediaImageSettings();
    } finally {
      setDeletingFormat(prev => ({ ...prev, [`${platform}-${format}`]: false }));
    }
  };

  // Handle Create Format Modal Open
  const openCreateFormatModal = (platform) => {
    setCreateFormatPlatform(platform);
    setNewFormatKey('');
    setNewFormatName('');
    setNewFormatWidth(1080);
    setNewFormatHeight(1080);
    setNewFormatIncludeSignature(true);
    setNewFormatIncludeText(true);
    setNewFormatDalleAspect('1024x1024');
    setNewFormatGptImageAspect('1024x1024');
    setNewFormatGeminiAspect('1:1');
    setShowCreateFormatModal(true);
  };

  // Handle Create Format
  const handleCreateFormat = async () => {
    // Validation
    if (!newFormatKey || !newFormatName) {
      setMessage({ 
        type: 'error', 
        text: 'Please provide both Format Key and Format Name' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    if (newFormatWidth <= 0 || newFormatHeight <= 0) {
      setMessage({ 
        type: 'error', 
        text: 'Width and Height must be positive numbers' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    // Check if format key already exists
    if (socialMediaImageSettings[createFormatPlatform] && 
        socialMediaImageSettings[createFormatPlatform][newFormatKey]) {
      setMessage({ 
        type: 'error', 
        text: 'Format key already exists. Please use a unique key.' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    try {
      setCreatingFormat(true);
      
      // Add to local state
      const newSettings = { ...socialMediaImageSettings };
      if (!newSettings[createFormatPlatform]) {
        newSettings[createFormatPlatform] = {};
      }
      
      // Determine output_style based on dimensions
      let outputStyle = 'square';
      if (newFormatWidth > newFormatHeight) {
        outputStyle = 'landscape';
      } else if (newFormatHeight > newFormatWidth) {
        outputStyle = 'portrait';
      }
      
      newSettings[createFormatPlatform][newFormatKey] = {
        width: newFormatWidth,
        height: newFormatHeight,
        include_signature: newFormatIncludeSignature,
        include_text: newFormatIncludeText,
        output_style: outputStyle,
        dalle_aspect_ratio: newFormatDalleAspect,
        gpt_image_aspect_ratio: newFormatGptImageAspect,
        gemini_aspect_ratio: newFormatGeminiAspect
      };
      
      setSocialMediaImageSettings(newSettings);
      
      // Save to backend
      await saveSocialMediaImageSettings();
      
      setMessage({ 
        type: 'success', 
        text: `Format "${newFormatName}" created successfully!` 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      
      // Close modal
      setShowCreateFormatModal(false);
    } catch (error) {
      console.error('Error creating format:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to create format. Please try again.' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setCreatingFormat(false);
    }
  };

  const loadReferenceImages = async () => {
    try {
      setReferenceImagesLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/reference-images`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.reference_images) {
          setReferenceImages(data.reference_images);
        }
      }
    } catch (error) {
      console.error('Error loading reference images:', error);
    } finally {
      setReferenceImagesLoading(false);
    }
  };

  const generateReferenceImages = async () => {
    setGeneratingReferenceImages(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/reference-images/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMessage({ 
            type: 'success', 
            text: data.message
          });
          // Reload reference images and status
          await loadReferenceImages();
          await loadReferenceImageStatus();
          setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } else {
          throw new Error(data.message || 'Failed to generate reference images');
        }
      } else {
        throw new Error('Failed to generate reference images');
      }
    } catch (error) {
      console.error('Error generating reference images:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to generate reference images. Please try again.' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setGeneratingReferenceImages(false);
    }
  };

  const loadReferenceImageStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reference-images/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.status) {
          setReferenceImageStatus(data.status);
        }
      }
    } catch (error) {
      console.error('Error loading reference image status:', error);
    }
  };

  const loadOpenAISettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/openai-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings) {
          setOpenaiSettings({
            openai_api_key: data.settings.openai_api_key || '',
            google_api_key: data.settings.google_api_key || '',
            anthropic_api_key: data.settings.anthropic_api_key || '',
            text_generation_model: data.settings.text_generation_model || 'gpt-4o',
            image_generation_model: data.settings.image_generation_model || 'dall-e-3',
            image_quality: data.settings.image_quality || 'hd',
            claude_model: data.settings.claude_model || 'claude-sonnet-4-5-20250514',
            use_openai_for_all_services: data.settings.use_openai_for_all_services || false,
            enable_ai_for_pdf_tools: data.settings.enable_ai_for_pdf_tools || false,
            // Deprecated but loaded for backward compatibility
            override_openrouter_text: data.settings.override_openrouter_text || false,
            override_openrouter_image: data.settings.override_openrouter_image || data.settings.override_openrouter || false
          });
          setOpenaiTextModels(data.text_models && data.text_models.length > 0 ? data.text_models : openaiTextModels);
          setOpenaiImageModels(data.image_models && data.image_models.length > 0 ? data.image_models : openaiImageModels);
          setOpenaiImageQualityOptions(data.image_quality_options && data.image_quality_options.length > 0 ? data.image_quality_options : openaiImageQualityOptions);
          setGptImageQualityOptions(data.gpt_image_quality_options && data.gpt_image_quality_options.length > 0 ? data.gpt_image_quality_options : gptImageQualityOptions);
        }
      }
    } catch (error) {
      console.error('Error loading OpenAI settings:', error);
    }
  };

  const saveOpenAISettings = async () => {
    setShowSavingModal(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/openai-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(openaiSettings)
      });

      if (response.ok) {
        // Scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error('Failed to save OpenAI settings');
      }
    } catch (error) {
      console.error('Error saving OpenAI settings:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to save OpenAI settings. Please try again.' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      // Hide modal after a delay
      setTimeout(() => {
        setShowSavingModal(false);
      }, 1000);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const settingsData = {
        openrouter_settings: openRouterSettings,
        social_settings: socialSettings
      };

      const response = await fetch(`${API_BASE_URL}/api/ai-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsData)
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: 'AI settings saved successfully!' 
        });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to save settings' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (service) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-settings/test/${service}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `${service} connection successful!` });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || `${service} connection failed` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Connection test failed' });
    } finally {
      setLoading(false);
    }
  };

  const testS3Connection = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/s3/test-connection`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: `✅ S3 Connection Successful! Connected to bucket: ${data.bucket} in region: ${data.region}` 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: `❌ S3 Connection Failed: ${data.message}` 
        });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: `❌ Connection Test Failed: ${error.message}` 
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleApiKeyVisibility = (key) => {
    setShowApiKeys(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // OpenRouter specific functions
  const loadAvailableModels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/models`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const modelsList = Object.entries(data.models || {});
        setAvailableModels(modelsList);
      }
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  const testOpenRouterConnection = async () => {
    setTestingConnection(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/models`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.models) {
          setOpenRouterSettings(prev => ({ ...prev, connection_status: 'connected' }));
          setMessage({ 
            type: 'success', 
            text: `✅ OpenRouter Connected Successfully! Found ${Object.keys(data.models).length} available models.` 
          });
          await loadAvailableModels();
        } else {
          setOpenRouterSettings(prev => ({ ...prev, connection_status: 'failed' }));
          setMessage({ 
            type: 'error', 
            text: '❌ Connection Failed: Invalid response format' 
          });
        }
      } else {
        setOpenRouterSettings(prev => ({ ...prev, connection_status: 'failed' }));
        const errorData = await response.json();
        setMessage({ 
          type: 'error', 
          text: `❌ Connection Failed: ${errorData.detail || 'Unknown error'}` 
        });
      }
    } catch (error) {
      setOpenRouterSettings(prev => ({ ...prev, connection_status: 'failed' }));
      setMessage({ 
        type: 'error', 
        text: `❌ Connection Failed: ${error.message}` 
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const disconnectOpenRouter = async () => {
    setOpenRouterSettings(prev => ({ ...prev, connection_status: 'unknown' }));
    setAvailableModels([]);
    setMessage({ 
      type: 'success', 
      text: '🔌 OpenRouter Disconnected' 
    });
  };

  const handleOpenRouterSettingChange = (field, value) => {
    setOpenRouterSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const cleanUsageData = async () => {
    setCleaningUsageData(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/openrouter/clean-usage-data`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: `✅ ${data.message}` 
        });
        setShowCleanUsageModal(false);
      } else {
        setMessage({ 
          type: 'error', 
          text: data.error || 'Failed to clean usage data' 
        });
      }
    } catch (error) {
      console.error('Error cleaning usage data:', error);
      setMessage({ 
        type: 'error', 
        text: 'Network error while cleaning usage data. Please try again.' 
      });
    } finally {
      setCleaningUsageData(false);
    }
  };

  const cleanOpenAIUsageData = async () => {
    setCleaningUsageData(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/openai/clean-usage-data`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: `✅ ${data.message}` 
        });
        setShowCleanUsageModal(false);
      } else {
        setMessage({ 
          type: 'error', 
          text: data.error || 'Failed to clean OpenAI usage data' 
        });
      }
    } catch (error) {
      console.error('Error cleaning OpenAI usage data:', error);
      setMessage({ 
        type: 'error', 
        text: 'Network error while cleaning usage data. Please try again.' 
      });
    } finally {
      setCleaningUsageData(false);
    }
  };

  const saveOpenRouterSettings = async () => {
    setShowSavingModal(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(openRouterSettings)
      });

      if (response.ok) {
        // Scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to save OpenRouter settings' });
      }
    } catch (error) {
      console.error('Error saving OpenRouter settings:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      // Hide modal after a delay
      setTimeout(() => {
        setShowSavingModal(false);
      }, 1000);
    }
  };

  // Individual template editing functions
  const openTemplateEditor = (templateType) => {
    let content = '';
    switch(templateType) {
      case 'email':
        content = signatureTemplates.aiEmailSignature;
        break;
      case 'photo':
        content = signatureTemplates.aiPhotoSignature;
        break;
      case 'sms':
        content = signatureTemplates.aiSmsSignature;
        break;
      case 'subject':
        content = signatureTemplates.aiEmailSubjectTemplate;
        break;
      default:
        content = '';
    }
    setTempTemplateContent(content);
    setEditingTemplate(templateType);
  };

  const closeTemplateEditor = () => {
    setEditingTemplate(null);
    setTempTemplateContent('');
    setTemplateSaving(false);
  };

  // Placeholder Modal Functions
  const openPlaceholderModal = () => {
    setPlaceholderModalOpen(true);
  };

  const closePlaceholderModal = () => {
    setPlaceholderModalOpen(false);
  };

  const insertPlaceholder = (placeholderCode) => {
    if (templateTextareaRef.current) {
      const element = templateTextareaRef.current;
      const currentValue = element.value;
      const cursorPosition = element.selectionStart || currentValue.length;
      
      // Insert at cursor position
      const newValue = currentValue.slice(0, cursorPosition) + 
                       placeholderCode + 
                       currentValue.slice(cursorPosition);
      
      // Update the state with the new value
      setTempTemplateContent(newValue);
      
      // Set cursor position after the inserted placeholder
      setTimeout(() => {
        if (element) {
          element.focus();
          element.setSelectionRange(cursorPosition + placeholderCode.length, cursorPosition + placeholderCode.length);
        }
      }, 0);
    }
    
    closePlaceholderModal();
  };

  const saveIndividualTemplate = async () => {
    setTemplateSaving(true);
    try {
      // Update the specific template
      const updatedTemplates = { ...signatureTemplates };
      switch(editingTemplate) {
        case 'email':
          updatedTemplates.aiEmailSignature = tempTemplateContent;
          break;
        case 'photo':
          updatedTemplates.aiPhotoSignature = tempTemplateContent;
          break;
        case 'sms':
          updatedTemplates.aiSmsSignature = tempTemplateContent;
          break;
        case 'subject':
          updatedTemplates.aiEmailSubjectTemplate = tempTemplateContent;
          break;
      }

      const response = await fetch(`${API_BASE_URL}/api/ai-signature-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedTemplates)
      });

      if (response.ok) {
        setSignatureTemplates(updatedTemplates);
        setMessage({ 
          type: 'success', 
          text: `${getTemplateDisplayName(editingTemplate)} template saved successfully!` 
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        closeTemplateEditor();
      } else {
        throw new Error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to save template. Please try again.' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setTemplateSaving(false);
    }
  };

  const getTemplateDisplayName = (templateType) => {
    switch(templateType) {
      case 'email': return 'AI Email Signature';
      case 'photo': return 'AI Photo Signature';
      case 'sms': return 'AI SMS Signature';
      case 'subject': return 'AI Email Subject Template';
      default: return 'Template';
    }
  };

  const getTemplatePlaceholders = (templateType) => {
    switch(templateType) {
      case 'email': return '[BUSINESS_NAME], [BUSINESS_ADDRESS], [PHONE_NUMBER], [BOOK_NOW_LINK], [WEBSITE_LINK], [CUSTOMER_NAME]';
      case 'photo': return '[BUSINESS_NAME], [PHONE_NUMBER]';
      case 'sms': return '[SMS_SIGNATURE]';
      case 'subject': return '[BUSINESS_SHORT_NAME] (from global settings), [CHATGPT_CONTENT_TITLE] (AI-generated from content)';
      default: return '';
    }
  };

  const handleSocialSettingChange = (platform, field, value) => {
    setSocialSettings(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value
      }
    }));
  };

  if (authLoading || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue"></div>
      </div>
    );
  }

  // Holiday Management Functions
  const fetchHolidays = async () => {
    setHolidaysLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/holidays`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHolidays(data);
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch holidays.' });
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setHolidaysLoading(false);
    }
  };

  const initializeDefaultHolidays = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/holidays/initialize-defaults`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: result.message });
        fetchHolidays();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to initialize holidays.' });
      }
    } catch (error) {
      console.error('Error initializing holidays:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  const refreshHolidayDates = async () => {
    try {
      setHolidaysLoading(true);
      setShowRefreshHolidayModal(false);
      
      const response = await fetch(`${API_BASE_URL}/api/holidays/refresh-dates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        let message = `Successfully refreshed holiday dates! Updated ${result.updated_count} of ${result.total_holidays} holidays.`;
        if (result.errors && result.errors.length > 0) {
          message += ` Some errors occurred: ${result.errors.join(', ')}`;
        }
        setMessage({ type: 'success', text: message });
        fetchHolidays();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to refresh holiday dates.' });
      }
    } catch (error) {
      console.error('Error refreshing holiday dates:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setHolidaysLoading(false);
    }
  };

  const handleHolidaySubmit = async (e) => {
    e.preventDefault();
    setHolidaysLoading(true);

    try {
      const url = showEditHolidayModal 
        ? `${API_BASE_URL}/api/holidays/${selectedHoliday.id}`
        : `${API_BASE_URL}/api/holidays`;
      
      const method = showEditHolidayModal ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(holidayFormData)
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: showEditHolidayModal ? 'Holiday updated successfully!' : 'Holiday added successfully!' 
        });
        resetHolidayForm();
        fetchHolidays();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to save holiday.' });
      }
    } catch (error) {
      console.error('Error saving holiday:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setHolidaysLoading(false);
    }
  };

  const handleDeleteHoliday = async () => {
    if (!selectedHoliday) return;

    setHolidaysLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/holidays/${selectedHoliday.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Holiday deleted successfully!' });
        setShowDeleteHolidayModal(false);
        fetchHolidays();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to delete holiday.' });
      }
    } catch (error) {
      console.error('Error deleting holiday:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setHolidaysLoading(false);
    }
  };

  const resetHolidayForm = () => {
    setHolidayFormData({
      name: '',
      date: '',
      month_day: '',
      is_recurring: true,
      is_enabled: true,
      category: 'general'
    });
    setShowAddHolidayModal(false);
    setShowEditHolidayModal(false);
    setSelectedHoliday(null);
  };

  const openEditHolidayModal = (holiday) => {
    setSelectedHoliday(holiday);
    setHolidayFormData({
      name: holiday.name || '',
      date: holiday.date || '',
      month_day: holiday.month_day || '',
      is_recurring: holiday.is_recurring,
      is_enabled: holiday.is_enabled,
      category: holiday.category || 'general'
    });
    setShowEditHolidayModal(true);
  };

  const handleHolidayDateChange = (date) => {
    setHolidayFormData(prev => ({
      ...prev,
      date: date,
      month_day: date ? date.substring(5) : ''
    }));
  };

  const getCategoryIcon = (category) => {
    const categoryObj = holidayCategories.find(cat => cat.value === category);
    return categoryObj ? categoryObj.icon : Calendar;
  };

  const filteredHolidays = holidays.filter(holiday => {
    const matchesSearch = holiday.name.toLowerCase().includes(holidaySearchTerm.toLowerCase());
    const matchesCategory = selectedHolidayCategory === 'all' || holiday.category === selectedHolidayCategory;
    return matchesSearch && matchesCategory;
  });

  // Access control - only allow Owner and Admin roles
  if (user && user.role !== 'owner' && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <X className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-4">AI Settings is only available to Owners and Admins.</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={() => window.location.href = '/ai-chatbot'}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-theme-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to AI Chatbot
            </button>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                AI Settings
              </h2>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation - Styled like Business Info */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-3 sm:p-6">
                {/* OpenRouter Settings Tab */}
                {activeTab === 'openrouter' && (
                  <div className="space-y-6">
                    {/* Connection Status and Test */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-gray-900">OpenRouter Configuration</h3>
                        <div className="flex items-center space-x-2">
                          {openRouterSettings.connection_status === 'connected' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Connected
                            </span>
                          )}
                          {openRouterSettings.connection_status === 'failed' && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Failed
                            </span>
                          )}
                          
                          {openRouterSettings.connection_status === 'connected' ? (
                            <button
                              onClick={disconnectOpenRouter}
                              className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 flex items-center"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Disconnect
                            </button>
                          ) : (
                            <button
                              onClick={testOpenRouterConnection}
                              disabled={testingConnection || !openRouterSettings.api_key}
                              className="text-white px-4 py-2 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                              style={{ 
                                backgroundColor: testingConnection || !openRouterSettings.api_key ? '#94a3b8' : 'var(--theme-primary)'
                              }}
                              onMouseEnter={(e) => {
                                if (!testingConnection && openRouterSettings.api_key) {
                                  e.target.style.backgroundColor = 'var(--theme-secondary)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!testingConnection && openRouterSettings.api_key) {
                                  e.target.style.backgroundColor = 'var(--theme-primary)';
                                }
                              }}
                            >
                              {testingConnection ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Zap className="w-4 h-4 mr-2" />
                              )}
                              Connect Now
                            </button>
                          )}
                        </div>
                      </div>

                      {/* API Key and App Name */}
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            OpenRouter API Key
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type={showApiKeys.openrouter_api_key ? 'text' : 'password'}
                              value={openRouterSettings.api_key}
                              onChange={(e) => handleOpenRouterSettingChange('api_key', e.target.value)}
                              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-theme-blue focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                              placeholder="sk-or-v1-..."
                            />
                            <button
                              type="button"
                              onClick={() => toggleApiKeyVisibility('openrouter_api_key')}
                              className="px-3 py-2 border border-gray-300 rounded-md text-gray-400 hover:text-gray-600"
                            >
                              {showApiKeys.openrouter_api_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Get your API key from <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:text-theme-blue">OpenRouter Dashboard</a>
                          </p>
                        </div>

                        {/* App Name Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            App Name
                          </label>
                          <input
                            type="text"
                            value={openRouterSettings.app_name}
                            onChange={(e) => handleOpenRouterSettingChange('app_name', e.target.value)}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-theme-blue focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="Enter your application name"
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            This name will be sent to OpenRouter for usage tracking and identification
                          </p>
                        </div>

                        {/* Provisioning Key Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Provisioning Key (Optional)
                          </label>
                          <div className="relative">
                            <input
                              type={showProvisioningKey ? "text" : "password"}
                              value={openRouterSettings.provisioning_key || ''}
                              onChange={(e) => handleOpenRouterSettingChange('provisioning_key', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-theme-blue focus:ring-blue-500 pr-10 text-sm placeholder:text-sm placeholder:font-medium"
                              placeholder="sk-or-prov-..."
                            />
                            <button
                              type="button"
                              onClick={() => setShowProvisioningKey(!showProvisioningKey)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              {showProvisioningKey ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Required for accessing activity data and detailed usage analytics. Get from <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:text-theme-blue">OpenRouter Dashboard</a>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Model Configuration */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h3 className="text-sm font-medium text-gray-900 mb-4">AI Model Configuration</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Text Generation Model */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Text Generation Model
                          </label>
                          <select
                            value={openRouterSettings.chat_model}
                            onChange={(e) => handleOpenRouterSettingChange('chat_model', e.target.value)}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-theme-blue focus:ring-blue-500 text-sm"
                          >
                            <option value="" className="text-sm">Select text model...</option>
                            {availableModels.filter(([modelId, modelInfo]) => modelInfo.category !== 'image_generation').map(([modelId, modelInfo]) => (
                              <option key={modelId} value={modelId} className="text-sm">
                                {modelInfo.name} - ${modelInfo.input_cost}/1M tokens
                              </option>
                            ))}
                          </select>
                          <p className="mt-1 text-xs text-gray-500">
                            Used for chat, social media, SMS, email, and marketing content
                          </p>
                        </div>

                        {/* Image Generation Model */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Image Generation Model
                          </label>
                          {availableModels.filter(([modelId, modelInfo]) => modelInfo.category === 'image_generation').length > 0 ? (
                            <select
                              value={openRouterSettings.image_model}
                              onChange={(e) => handleOpenRouterSettingChange('image_model', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-theme-blue focus:ring-blue-500 text-sm"
                            >
                              <option value="" className="text-sm">Select image model...</option>
                              {availableModels.filter(([modelId, modelInfo]) => modelInfo.category === 'image_generation').map(([modelId, modelInfo]) => (
                                <option key={modelId} value={modelId} className="text-sm">
                                  {modelInfo.name} - ${modelInfo.input_cost}/1M tokens
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                              Image generation models not accessible with current API key
                            </div>
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            {availableModels.filter(([modelId, modelInfo]) => modelInfo.category === 'image_generation').length > 0 
                              ? "Google Gemini 2.5 Flash Image Preview available for image generation"
                              : "Contact OpenRouter support to enable image generation models for your account"
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center">
                      {/* Clean Usage Data Button */}
                      <button
                        onClick={() => setShowCleanUsageModal(true)}
                        disabled={cleaningUsageData || loading}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors text-sm"
                      >
                        {cleaningUsageData ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <X className="w-4 h-4 mr-2" />
                        )}
                        Clean Usage Data
                      </button>

                      {/* Save Button */}
                      <button
                        onClick={saveOpenRouterSettings}
                        disabled={loading}
                        className="text-white px-6 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                        style={{ 
                          backgroundColor: loading ? '#94a3b8' : 'var(--theme-primary)'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = 'var(--theme-secondary)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.target.style.backgroundColor = 'var(--theme-primary)';
                          }
                        }}
                      >
                        {loading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Save Settings
                      </button>
                    </div>
                  </div>
                )}

                {/* OpenAI Settings Tab */}
                {activeTab === 'openai' && (
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex">
                        <Settings className="h-5 w-5 text-green-400 mt-0.5 mr-2" />
                        <div>
                          <h4 className="text-sm font-medium text-green-800">OpenAI Direct Integration</h4>
                          <p className="text-sm text-green-700 mt-1">
                            Use OpenAI directly for all AI services (text and image generation) instead of OpenRouter. Configure your API keys and preferred models.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="space-y-6">
                        {/* API Key Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            OpenAI API Key
                          </label>
                          <div className="relative">
                            <input
                              type={showOpenAIKey ? "text" : "password"}
                              value={openaiSettings.openai_api_key}
                              onChange={(e) => setOpenaiSettings({
                                ...openaiSettings,
                                openai_api_key: e.target.value
                              })}
                              placeholder="sk-proj-..."
                              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm placeholder:text-sm placeholder:font-medium"
                            />
                            <button
                              type="button"
                              onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              {showOpenAIKey ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Get your API key from: <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:underline">https://platform.openai.com/api-keys</a>
                          </p>
                        </div>

                        {/* Google API Key Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Google AI API Key (for Nano Banana)
                          </label>
                          <div className="relative">
                            <input
                              type={showGoogleKey ? "text" : "password"}
                              value={openaiSettings.google_api_key}
                              onChange={(e) => setOpenaiSettings({
                                ...openaiSettings,
                                google_api_key: e.target.value
                              })}
                              placeholder="AIzaSy..."
                              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm placeholder:text-sm placeholder:font-medium"
                            />
                            <button
                              type="button"
                              onClick={() => setShowGoogleKey(!showGoogleKey)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              {showGoogleKey ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Get your API key from: <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:underline">https://aistudio.google.com/app/apikey</a>
                          </p>
                        </div>

                        {/* Anthropic/Claude API Key Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Anthropic API Key (for Claude)
                          </label>
                          <div className="relative">
                            <input
                              type={showAnthropicKey ? "text" : "password"}
                              value={openaiSettings.anthropic_api_key}
                              onChange={(e) => setOpenaiSettings({
                                ...openaiSettings,
                                anthropic_api_key: e.target.value
                              })}
                              placeholder="sk-ant-api03-..."
                              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm placeholder:text-sm placeholder:font-medium"
                            />
                            <button
                              type="button"
                              onClick={() => setShowAnthropicKey(!showAnthropicKey)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              {showAnthropicKey ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Get your API key from: <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-theme-blue hover:underline">https://console.anthropic.com/settings/keys</a>
                          </p>
                        </div>

                        {/* Claude Model Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Default Claude Model
                          </label>
                          <select
                            value={openaiSettings.claude_model}
                            onChange={(e) => setOpenaiSettings({
                              ...openaiSettings,
                              claude_model: e.target.value
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                          >
                            {claudeModels.map((model) => (
                              <option key={model.value} value={model.value}>
                                {model.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Select the default Claude model to use for AI text generation
                          </p>
                        </div>

                        {/* Master Override Setting */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="use-openai-for-all-services"
                              checked={openaiSettings.use_openai_for_all_services}
                              onChange={(e) => setOpenaiSettings({
                                ...openaiSettings,
                                use_openai_for_all_services: e.target.checked
                              })}
                              className="mr-3 rounded border-gray-300 text-theme-blue focus:ring-blue-500"
                            />
                            <div>
                              <label htmlFor="use-openai-for-all-services" className="text-sm font-medium text-gray-900">
                                Use OpenAI for All AI Services
                              </label>
                              <p className="text-xs text-gray-600 mt-1">
                                When enabled, all AI services (text and image generation) will use OpenAI directly instead of OpenRouter. This applies organization-wide.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Enable AI for PDF Tools */}
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="enable-ai-for-pdf-tools"
                              checked={openaiSettings.enable_ai_for_pdf_tools}
                              onChange={(e) => setOpenaiSettings({
                                ...openaiSettings,
                                enable_ai_for_pdf_tools: e.target.checked
                              })}
                              className="mr-3 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <div>
                              <label htmlFor="enable-ai-for-pdf-tools" className="text-sm font-medium text-gray-900">
                                Enable AI for PDF Tools
                              </label>
                              <p className="text-xs text-gray-600 mt-1">
                                Use AI-powered intelligence for PDF page number detection and removal. AI can accurately distinguish between page numbers and document content (e.g., dollar amounts, section numbers). More accurate than pattern matching.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Individual Model Settings (used when master override is enabled) */}
                        <div className={`space-y-6 ${!openaiSettings.use_openai_for_all_services ? 'opacity-50' : ''}`}>
                          <div className="text-sm text-gray-600 mb-4">
                            {openaiSettings.use_openai_for_all_services 
                              ? "Configure the specific OpenAI models to use:" 
                              : "Enable master override above to configure these models:"
                            }
                          </div>

                        {/* Text Generation Model */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Text Generation Model
                            </label>
                          </div>
                          <select
                            value={openaiSettings.text_generation_model}
                            onChange={(e) => setOpenaiSettings({
                              ...openaiSettings,
                              text_generation_model: e.target.value
                            })}
                            disabled={!openaiSettings.use_openai_for_all_services}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            {openaiTextModels.map((model) => (
                              <option key={model.value} value={model.value} className="text-sm">
                                {model.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Image Generation Model */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Image Generation Model
                            </label>
                          </div>
                          <select
                            value={openaiSettings.image_generation_model}
                            onChange={(e) => {
                              const newModel = e.target.value;
                              setOpenaiSettings({
                                ...openaiSettings,
                                image_generation_model: newModel,
                                // Set default quality based on model
                                image_quality: newModel === 'gpt-image-1' ? 'high' : 
                                              (newModel === 'dall-e-3' || newModel === 'dall-e-2') ? 'hd' : 
                                              openaiSettings.image_quality
                              });
                            }}
                            disabled={!openaiSettings.use_openai_for_all_services}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            {openaiImageModels.map((model) => (
                              <option key={model.value} value={model.value} className="text-sm">
                                {model.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Image Quality - Show for DALL-E and GPT Image 1 models */}
                        {!openaiSettings.image_generation_model.startsWith('google/') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {openaiSettings.image_generation_model === 'gpt-image-1' ? 
                                'Image Quality (GPT Image 1)' : 
                                'Image Quality (DALL-E)'}
                            </label>
                            <select
                              value={openaiSettings.image_quality}
                              onChange={(e) => setOpenaiSettings({
                                ...openaiSettings,
                                image_quality: e.target.value
                              })}
                              disabled={!openaiSettings.use_openai_for_all_services}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {(openaiSettings.image_generation_model === 'gpt-image-1' ? 
                                gptImageQualityOptions : 
                                openaiImageQualityOptions
                              ).map((quality) => (
                                <option key={quality.value} value={quality.value} className="text-sm">
                                  {quality.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        </div>
                        
                        {/* Override Status Messages */}
                        <div className="text-xs text-gray-500 space-y-1">
                          {openaiSettings.use_openai_for_all_services ? (
                            <>
                              <div className="text-theme-blue font-medium">
                                ✅ OpenAI Direct Integration Enabled
                              </div>
                              <div>
                                📝 Text Generation: OpenAI {openaiSettings.text_generation_model.toUpperCase()}
                              </div>
                              <div>
                                🖼️ Image Generation: {
                                  openaiSettings.image_generation_model.startsWith('google/') ? 
                                    'Google Gemini (Nano Banana)' :
                                    openaiSettings.image_generation_model === 'gpt-image-1' ? 
                                      'OpenAI GPT Image 1' :
                                      'OpenAI DALL-E'
                                }
                              </div>
                            </>
                          ) : (
                            <div className="text-gray-500">
                              ⚪ Using OpenRouter for all AI services (recommended for cost efficiency)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center">
                      {/* Clean Usage Data Button */}
                      <button
                        onClick={() => setShowCleanUsageModal(true)}
                        disabled={cleaningUsageData || openaiSettingsLoading}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors text-sm"
                      >
                        {cleaningUsageData ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <X className="w-4 h-4 mr-2" />
                        )}
                        Clean Usage Data
                      </button>

                      {/* Save Button */}
                      <button
                        onClick={saveOpenAISettings}
                        disabled={openaiSettingsLoading}
                        className="text-white px-6 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors bg-green-600 hover:bg-green-700"
                      >
                        {openaiSettingsLoading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Save Configurations
                      </button>
                    </div>
                  </div>
                )}

                {/* Prompts Tab */}
                {activeTab === 'prompts' && (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex">
                        <Sparkles className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-800">AI Content Generation Prompts</h4>
                          <p className="text-sm text-blue-700 mt-1">
                            Define prompts used for automated content generation from web scraping. These prompts are customized for each business type.
                          </p>
                          <p className="text-sm text-blue-600 mt-2 font-medium">
                            Current Business Type: <span className="text-blue-800">{prompts.current_business_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Veterinary Hospital'}</span>
                            <span className="text-xs text-blue-500 ml-2">(Set in Business Details page)</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Business Type Tabs */}
                    <div className="border-b border-gray-200">
                      <nav className="-mb-px flex space-x-4 overflow-x-auto">
                        {[
                          { id: 'veterinary_hospital', label: 'Veterinary Hospital' },
                          { id: 'restaurant', label: 'Restaurant' },
                          { id: 'grocery_store', label: 'Grocery Store' },
                          { id: 'restaurant_grocery_store', label: 'Restaurant & Grocery' }
                        ].map((type) => (
                          <button
                            key={type.id}
                            onClick={() => setSelectedBusinessType(type.id)}
                            className={`whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                              selectedBusinessType === type.id
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } ${prompts.current_business_type === type.id ? 'bg-green-50' : ''}`}
                          >
                            {type.label}
                            {prompts.current_business_type === type.id && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            )}
                          </button>
                        ))}
                      </nav>
                    </div>

                    {/* OpenAI Content Processing Prompt */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          OpenAI Content Processing Prompt
                        </label>
                        <p className="text-xs text-gray-600 mb-3">
                          This prompt is used with OpenAI GPT-4o to process and structure the scraped content before HTML generation. 
                          It extracts key information and prepares it for the next step.
                        </p>
                        <textarea
                          value={prompts[selectedBusinessType]?.openai_content_processing || ''}
                          onChange={(e) => setPrompts({
                            ...prompts,
                            [selectedBusinessType]: {
                              ...prompts[selectedBusinessType],
                              openai_content_processing: e.target.value
                            }
                          })}
                          rows={10}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                          placeholder="Enter the OpenAI prompt for content processing... (Leave empty to use default)"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Available variables: {'{SCRAPED_CONTENT}'}, {'{PAGE_URL}'}, {'{WEB_RESEARCH_SECTION}'}
                        </p>
                      </div>
                    </div>

                    {/* Claude HTML Generation Prompt */}
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Claude Sonnet 4.5 HTML Generation Prompt
                        </label>
                        <p className="text-xs text-gray-600 mb-3">
                          This prompt is used with Claude Sonnet 4.5 to generate beautiful HTML content from the processed data. 
                          It creates the final page structure with proper formatting.
                        </p>
                        <textarea
                          value={prompts[selectedBusinessType]?.claude_html_generation || ''}
                          onChange={(e) => setPrompts({
                            ...prompts,
                            [selectedBusinessType]: {
                              ...prompts[selectedBusinessType],
                              claude_html_generation: e.target.value
                            }
                          })}
                          rows={10}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                          placeholder="Enter the Claude prompt for HTML generation... (Leave empty to use default)"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Available variables: {'{PROCESSED_CONTENT}'}, {'{PAGE_URL}'}
                        </p>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-4 border-t border-gray-200">
                      <button
                        onClick={savePrompts}
                        disabled={savingPrompts}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingPrompts ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save All Prompts
                          </>
                        )}
                      </button>
                    </div>

                    {/* Default Prompts Info */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">How it works</h4>
                      <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                        <li>User enters a URL in the page template editor</li>
                        <li>System scrapes the webpage for content and main image</li>
                        <li><strong>OpenAI GPT-4o</strong> processes the scraped content using the first prompt</li>
                        <li><strong>Claude Sonnet 4.5</strong> generates structured content using the second prompt</li>
                        <li>Generated content populates: Title, Badge Text, Description, and Main Content</li>
                      </ol>
                      <p className="text-xs text-gray-500 mt-3">
                        <strong>Note:</strong> If prompts are left empty, the system will use sensible defaults for each business type.
                      </p>
                    </div>
                  </div>
                )}

                {/* Social Media Settings Tab */}
                {activeTab === 'social' && (
                  <div className="space-y-8">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 mr-2" />
                        <div>
                          <h4 className="text-sm font-medium text-yellow-800">Coming Soon</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            Social media API integrations are being developed. For now, you can prepare your API credentials.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Facebook Settings */}
                    <div className="border rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <Facebook className="h-4 w-4 mr-2 text-gray-600" />
                        Facebook
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">App ID</label>
                          <input
                            type="text"
                            value={socialSettings.facebook.app_id}
                            onChange={(e) => handleSocialSettingChange('facebook', 'app_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="Your Facebook App ID"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Page ID</label>
                          <input
                            type="text"
                            value={socialSettings.facebook.page_id}
                            onChange={(e) => handleSocialSettingChange('facebook', 'page_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="Your Facebook Page ID"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Instagram Settings */}
                    <div className="border rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <Instagram className="h-4 w-4 mr-2 text-gray-600" />
                        Instagram
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
                          <input
                            type="password"
                            value={socialSettings.instagram.access_token}
                            onChange={(e) => handleSocialSettingChange('instagram', 'access_token', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="Instagram Access Token"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                          <input
                            type="text"
                            value={socialSettings.instagram.user_id}
                            onChange={(e) => handleSocialSettingChange('instagram', 'user_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="Instagram User ID"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Twitter/X Settings */}
                    <div className="border rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <Twitter className="h-4 w-4 mr-2 text-gray-600" />
                        X (Twitter)
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                          <input
                            type="password"
                            value={socialSettings.twitter.api_key}
                            onChange={(e) => handleSocialSettingChange('twitter', 'api_key', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="Twitter API Key"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
                          <input
                            type="password"
                            value={socialSettings.twitter.api_secret}
                            onChange={(e) => handleSocialSettingChange('twitter', 'api_secret', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="Twitter API Secret"
                          />
                        </div>
                      </div>
                    </div>

                    {/* TikTok Settings */}
                    <div className="border rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <Video className="h-4 w-4 mr-2 text-gray-600" />
                        TikTok
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Client Key</label>
                          <input
                            type="password"
                            value={socialSettings.tiktok.client_key}
                            onChange={(e) => handleSocialSettingChange('tiktok', 'client_key', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="TikTok Client Key"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
                          <input
                            type="password"
                            value={socialSettings.tiktok.client_secret}
                            onChange={(e) => handleSocialSettingChange('tiktok', 'client_secret', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="TikTok Client Secret"
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
                          <input
                            type="password"
                            value={socialSettings.tiktok.access_token}
                            onChange={(e) => handleSocialSettingChange('tiktok', 'access_token', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="TikTok Access Token"
                          />
                        </div>
                      </div>
                    </div>

                    {/* WhatsApp Settings */}
                    <div className="border rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <MessageCircle className="h-4 w-4 mr-2 text-gray-600" />
                        WhatsApp Business
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Business Account ID</label>
                          <input
                            type="text"
                            value={socialSettings.whatsapp.business_account_id}
                            onChange={(e) => handleSocialSettingChange('whatsapp', 'business_account_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="WhatsApp Business Account ID"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number ID</label>
                          <input
                            type="text"
                            value={socialSettings.whatsapp.phone_number_id}
                            onChange={(e) => handleSocialSettingChange('whatsapp', 'phone_number_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder:text-sm placeholder:font-medium"
                            placeholder="WhatsApp Phone Number ID"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Signature Templates Tab */}
                {activeTab === 'signature-templates' && (
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex">
                        <Settings className="h-5 w-5 text-green-400 mt-0.5 mr-2" />
                        <div>
                          <h4 className="text-sm font-medium text-green-800">AI Signature Templates</h4>
                          <p className="text-sm text-green-700 mt-1">
                            Configure signature templates for consistent branding across all AI-generated content including emails, images, and SMS messages.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Template Cards with Edit Buttons */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      
                      {/* AI Email Signature Card */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-900 flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-gray-600" />
                            AI Email Signature
                          </h4>
                          <button
                            onClick={() => openTemplateEditor('email')}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          Signature template for all AI-generated emails (chatbot and agents).
                        </p>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <pre className="text-sm text-gray-600 whitespace-pre-wrap font-mono">
                            {signatureTemplates.aiEmailSignature || 'Best regards,\n[BUSINESS_NAME]\n[BUSINESS_ADDRESS]\n[PHONE_NUMBER]\n[BOOK_NOW_LINK]\n[WEBSITE_LINK]\n----'}
                          </pre>
                        </div>
                      </div>

                      {/* AI Photo Signature Card */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-900 flex items-center">
                            <Image className="h-4 w-4 mr-2 text-gray-600" />
                            AI Photo Signature
                          </h4>
                          <button
                            onClick={() => openTemplateEditor('photo')}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          Adds consistent branding to AI-generated images.
                        </p>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 font-mono">
                            {signatureTemplates.aiPhotoSignature || '[BUSINESS_NAME] • Call [PHONE_NUMBER] for expert veterinary care'}
                          </p>
                        </div>
                      </div>

                      {/* AI SMS Signature Card */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-900 flex items-center">
                            <MessageSquare className="h-4 w-4 mr-2 text-gray-600" />
                            AI SMS Signature
                          </h4>
                          <button
                            onClick={() => openTemplateEditor('sms')}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          Signature for AI-generated SMS content using global placeholder.
                        </p>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 font-mono">
                            {signatureTemplates.aiSmsSignature || '[SMS_SIGNATURE]'}
                          </p>
                        </div>
                      </div>

                      {/* AI Email Subject Template Card */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-medium text-gray-900 flex items-center">
                            <FileText className="h-4 w-4 mr-2 text-gray-600" />
                            AI Email Subject Template
                          </h4>
                          <button
                            onClick={() => openTemplateEditor('subject')}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          Template for AI-generated email subject lines with dynamic content.
                        </p>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-600 font-mono">
                            {signatureTemplates.aiEmailSubjectTemplate || '[BUSINESS_SHORT_NAME]: [CHATGPT_CONTENT_TITLE]'}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            Example: "VetClinic Pro: Important Vaccination Reminder"
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Image Formats Tab */}
                {activeTab === 'social-media-image-settings' && (
                  <div className="space-y-6">
                    {/* Header with Title and Filter */}
                    <div className="bg-gray-100 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <h2 className="text-base font-medium text-gray-900">
                          Image Formats
                        </h2>
                        <div className="flex items-center space-x-3">
                          <select
                            value={platformFilter}
                            onChange={(e) => setPlatformFilter(e.target.value)}
                            className="px-3 py-1.5 text-sm border border-gray-300 bg-white text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 appearance-none pr-8"
                            style={{ 
                              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23374151' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                              backgroundPosition: 'right 6px center',
                              backgroundRepeat: 'no-repeat',
                              backgroundSize: '14px'
                            }}
                          >
                            <option value="all">All Platforms</option>
                            <option value="instagram">Instagram</option>
                            <option value="facebook">Facebook</option>
                            <option value="tiktok">TikTok</option>
                            <option value="x">X (Twitter)</option>
                            <option value="whatsapp">WhatsApp</option>
                            <option value="popup">Landing Page Popups</option>
                            <option value="sms">SMS</option>
                            <option value="email">Email</option>
                            <option value="marketing">Promotion Material</option>
                          </select>
                          <button
                            onClick={generateReferenceImages}
                            disabled={generatingReferenceImages}
                            className="px-3 py-1.5 text-sm bg-cyan-500 hover:bg-cyan-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                          >
                            {generatingReferenceImages ? (
                              <>
                                <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Image className="w-3.5 h-3.5 mr-1.5" />
                                Generate Templates
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Platform Settings Cards */}
                    <div className="space-y-6">
                      {Object.keys(socialMediaImageSettings)
                        .filter(platform => platformFilter === 'all' || platform === platformFilter)
                        .map((platform) => {
                        const platformNames = {
                          instagram: 'Instagram',
                          facebook: 'Facebook', 
                          tiktok: 'TikTok',
                          x: 'X (Twitter)',
                          whatsapp: 'WhatsApp',
                          sms: 'SMS',
                          email: 'Email',
                          popup: 'Landing Page Popups',
                          marketing: 'Promotion Material'
                        };
                        
                        const platformIcons = {
                          instagram: Instagram,
                          facebook: Facebook,
                          tiktok: Video,
                          x: Twitter,
                          whatsapp: MessageCircle,
                          sms: MessageSquare,
                          email: Mail,
                          popup: MessageSquare,
                          marketing: Megaphone
                        };

                        return (
                          <div key={platform} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="font-medium text-gray-900 flex items-center">
                                {React.createElement(platformIcons[platform], { className: "h-4 w-4 mr-2 text-gray-600" })}
                                {platformNames[platform]}
                              </h4>
                              <button
                                onClick={() => openCreateFormatModal(platform)}
                                className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Create Format
                              </button>
                            </div>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              {Object.keys(socialMediaImageSettings[platform]).map((format) => {
                                const formatNames = {
                                  post: 'Post',
                                  profile_picture: 'Profile Picture',
                                  landscape: 'Landscape',
                                  square: 'Square',
                                  story_reels: 'Story/Reels',
                                  cover_photo: 'Cover Photo',
                                  portrait: 'Portrait',
                                  poster: 'Poster',
                                  flyer: 'Flyer'
                                };

                                return (
                                  <div key={format} className="border border-gray-200 rounded-lg p-5 bg-gray-50 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-3">
                                      <h5 className="font-medium text-sm text-gray-900">
                                        {formatNames[format]}
                                      </h5>
                                    </div>
                                    
                                    <div className="space-y-3">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Width (px)
                                          </label>
                                          <input
                                            type="number"
                                            value={socialMediaImageSettings[platform][format].width}
                                            onChange={(e) => {
                                              const newSettings = { ...socialMediaImageSettings };
                                              newSettings[platform][format].width = parseInt(e.target.value) || 0;
                                              setSocialMediaImageSettings(newSettings);
                                            }}
                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Height (px)
                                          </label>
                                          <input
                                            type="number"
                                            value={socialMediaImageSettings[platform][format].height}
                                            onChange={(e) => {
                                              const newSettings = { ...socialMediaImageSettings };
                                              newSettings[platform][format].height = parseInt(e.target.value) || 0;
                                              setSocialMediaImageSettings(newSettings);
                                            }}
                                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          />
                                        </div>
                                      </div>
                                      
                                      <div className="flex items-center">
                                        <input
                                          type="checkbox"
                                          id={`${platform}-${format}-signature`}
                                          checked={socialMediaImageSettings[platform][format].include_signature}
                                          onChange={(e) => {
                                            const newSettings = { ...socialMediaImageSettings };
                                            newSettings[platform][format].include_signature = e.target.checked;
                                            setSocialMediaImageSettings(newSettings);
                                          }}
                                          className="mr-2 rounded border-gray-300 text-theme-blue focus:ring-blue-500"
                                        />
                                        <label 
                                          htmlFor={`${platform}-${format}-signature`}
                                          className="text-xs text-gray-700"
                                        >
                                          Include AI Photo Signature
                                        </label>
                                      </div>
                                      
                                      <div className="flex items-center">
                                        <input
                                          type="checkbox"
                                          id={`${platform}-${format}-text`}
                                          checked={socialMediaImageSettings[platform][format].include_text}
                                          onChange={(e) => {
                                            const newSettings = { ...socialMediaImageSettings };
                                            newSettings[platform][format].include_text = e.target.checked;
                                            setSocialMediaImageSettings(newSettings);
                                          }}
                                          className="mr-2 rounded border-gray-300 text-theme-blue focus:ring-blue-500"
                                        />
                                        <label 
                                          htmlFor={`${platform}-${format}-text`}
                                          className="text-xs text-gray-700"
                                        >
                                          Include Text on Image
                                        </label>
                                      </div>
                                      
                                      {/* Model-Specific Aspect Ratios */}
                                      <div className="space-y-3 border-t border-gray-200 pt-3 mt-3">
                                        <div className="text-xs font-semibold text-gray-700 mb-2">
                                          AI Model Aspect Ratios
                                        </div>
                                        
                                        {/* DALL-E 3 Aspect Ratio */}
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            DALL-E 3 Size
                                          </label>
                                          <select
                                            value={socialMediaImageSettings[platform][format].dalle_aspect_ratio || "1024x1024"}
                                            onChange={(e) => {
                                              const newSettings = { ...socialMediaImageSettings };
                                              newSettings[platform][format].dalle_aspect_ratio = e.target.value;
                                              setSocialMediaImageSettings(newSettings);
                                            }}
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          >
                                            <option value="1024x1024">1024×1024 (Square)</option>
                                            <option value="1792x1024">1792×1024 (Landscape)</option>
                                            <option value="1024x1792">1024×1792 (Portrait)</option>
                                          </select>
                                        </div>
                                        
                                        {/* GPT Image 1 Aspect Ratio */}
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            GPT Image 1 Size
                                          </label>
                                          <select
                                            value={socialMediaImageSettings[platform][format].gpt_image_aspect_ratio || "1024x1024"}
                                            onChange={(e) => {
                                              const newSettings = { ...socialMediaImageSettings };
                                              newSettings[platform][format].gpt_image_aspect_ratio = e.target.value;
                                              setSocialMediaImageSettings(newSettings);
                                            }}
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          >
                                            <option value="1024x1024">1024×1024 (Square)</option>
                                            <option value="1536x1024">1536×1024 (Landscape)</option>
                                            <option value="1024x1536">1024×1536 (Portrait)</option>
                                          </select>
                                        </div>
                                        
                                        {/* Gemini (Nano Banana) Aspect Ratio */}
                                        <div>
                                          <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Nano Banana (Gemini) Aspect Ratio
                                          </label>
                                          <select
                                            value={socialMediaImageSettings[platform][format].gemini_aspect_ratio || "1:1"}
                                            onChange={(e) => {
                                              const newSettings = { ...socialMediaImageSettings };
                                              newSettings[platform][format].gemini_aspect_ratio = e.target.value;
                                              setSocialMediaImageSettings(newSettings);
                                            }}
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          >
                                            <option value="1:1">1:1 (Square)</option>
                                            <option value="16:9">16:9 (Landscape)</option>
                                            <option value="9:16">9:16 (Portrait)</option>
                                            <option value="4:3">4:3 (Standard)</option>
                                            <option value="3:4">3:4 (Portrait)</option>
                                            <option value="2:3">2:3 (Portrait)</option>
                                            <option value="3:2">3:2 (Landscape)</option>
                                          </select>
                                        </div>
                                      </div>
                                      
                                      {/* Individual Save and Delete Buttons */}
                                      <div className="pt-3 border-t border-gray-200 mt-3 flex gap-2">
                                        <button
                                          onClick={() => saveIndividualFormatSettings(platform, format)}
                                          disabled={formatSavingStates[`${platform}-${format}`]}
                                          className={`flex-1 text-white text-xs px-3 py-2 rounded-md flex items-center justify-center transition-colors ${
                                            formatSavingStates[`${platform}-${format}`]
                                              ? 'bg-gray-400 cursor-not-allowed'
                                              : 'bg-cyan-600 hover:bg-cyan-700'
                                          }`}
                                        >
                                          {formatSavingStates[`${platform}-${format}`] ? (
                                            <>
                                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                              Saving...
                                            </>
                                          ) : (
                                            <>
                                              <Save className="w-3 h-3 mr-1" />
                                              Save
                                            </>
                                          )}
                                        </button>
                                        
                                        <button
                                          onClick={() => handleDeleteFormat(platform, format, formatNames[format] || format)}
                                          disabled={deletingFormat[`${platform}-${format}`]}
                                          className={`text-white text-xs px-3 py-2 rounded-md flex items-center justify-center transition-colors ${
                                            deletingFormat[`${platform}-${format}`]
                                              ? 'bg-gray-400 cursor-not-allowed'
                                              : 'bg-red-600 hover:bg-red-700'
                                          }`}
                                        >
                                          {deletingFormat[`${platform}-${format}`] ? (
                                            <RefreshCw className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Trash2 className="w-3 h-3" />
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Global save button removed - using individual save buttons now */}
                  </div>
                )}

                {/* Manage Holidays Tab */}
                {activeTab === 'manage-holidays' && (
                  <div className="space-y-6">
                    {/* Holiday Management Content */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      {/* Header */}
                      <div className="bg-white border-b border-gray-200">
                        <div className="p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-3">
                                Holidays
                                <span className="text-xs font-normal text-gray-500">
                                  Total: {holidays.length}
                                </span>
                              </h3>
                            </div>
                            <div className="flex flex-row items-center gap-2">
                              {holidays.length === 0 ? (
                                <button
                                  onClick={initializeDefaultHolidays}
                                  className="text-white text-sm px-3 py-1.5 rounded-md transition-colors hover:opacity-90 flex items-center font-medium"
                                  style={{ backgroundColor: '#059669' }}
                                >
                                  <Sparkles className="h-4 w-4 mr-1.5" />
                                  Initialize Defaults
                                </button>
                              ) : (
                                <button
                                  onClick={() => setShowRefreshHolidayModal(true)}
                                  disabled={holidaysLoading}
                                  className="text-white text-sm px-3 py-1.5 rounded-md transition-colors hover:opacity-90 flex items-center font-medium disabled:opacity-50"
                                  style={{ backgroundColor: '#10b981' }}
                                >
                                  <Calendar className="h-4 w-4 mr-1.5" />
                                  Refresh Dates
                                </button>
                              )}
                              <button
                                onClick={() => setShowAddHolidayModal(true)}
                                className="text-white text-sm px-3 py-1.5 rounded-md transition-colors hover:opacity-90 flex items-center font-medium"
                                style={{ backgroundColor: 'var(--theme-primary)' }}
                              >
                                <Plus className="h-4 w-4 mr-1.5" />
                                Add Holiday
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Search and Filters */}
                      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                        <div className="flex flex-row items-center gap-3 flex-wrap">
                          {/* Search Input */}
                          <div className="relative flex-1 min-w-[200px] max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                              type="text"
                              placeholder="Search holidays..."
                              value={holidaySearchTerm}
                              onChange={(e) => setHolidaySearchTerm(e.target.value)}
                              className="w-full pl-10 pr-4 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
                            />
                          </div>
                          
                          {/* Category Filter Buttons */}
                          <div className="flex space-x-2">
                            {holidayCategories.map((category) => {
                              const Icon = category.icon;
                              return (
                                <button
                                  key={category.value}
                                  onClick={() => setSelectedHolidayCategory(category.value)}
                                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center space-x-1.5 ${
                                    selectedHolidayCategory === category.value
                                      ? 'bg-cyan-100 text-cyan-700'
                                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                  }`}
                                >
                                  <Icon className="h-3.5 w-3.5" />
                                  <span>{category.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        {/* Holiday List */}
                        {holidaysLoading ? (
                          <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
                            <p className="text-gray-500 mt-4">Loading holidays...</p>
                          </div>
                        ) : filteredHolidays.length === 0 ? (
                          <div className="text-center py-12">
                            <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                            <p className="text-gray-500 text-lg">No holidays found</p>
                            <p className="text-gray-400 text-sm mt-2">
                              {holidaySearchTerm || selectedHolidayCategory !== 'all' 
                                ? 'Try adjusting your search or filters' 
                                : 'Add your first holiday to get started'}
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {filteredHolidays.map((holiday) => {
                              const Icon = getCategoryIcon(holiday.category);
                              return (
                                <div key={holiday.id} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                      <div className={`p-1.5 rounded-full ${
                                        holiday.category === 'pet' ? 'bg-pink-100' :
                                        holiday.category === 'veterinary' ? 'bg-blue-100' :
                                        holiday.category === 'family' ? 'bg-purple-100' :
                                        'bg-gray-100'
                                      }`}>
                                        <Icon className={`h-3.5 w-3.5 ${
                                          holiday.category === 'pet' ? 'text-pink-600' :
                                          holiday.category === 'veterinary' ? 'text-theme-blue' :
                                          holiday.category === 'family' ? 'text-purple-600' :
                                          'text-gray-600'
                                        }`} />
                                      </div>
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-900">{holiday.name}</h4>
                                        <p className="text-xs text-gray-500">{holiday.date}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <button
                                        onClick={() => openEditHolidayModal(holiday)}
                                        className="text-cyan-600 hover:text-cyan-800 p-1"
                                      >
                                        <Edit2 className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          setSelectedHoliday(holiday);
                                          setShowDeleteHolidayModal(true);
                                        }}
                                        className="text-red-600 hover:text-red-800 p-1"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                        holiday.is_enabled 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {holiday.is_enabled ? 'Enabled' : 'Disabled'}
                                      </span>
                                      <span className="text-xs text-gray-500 capitalize">
                                        {holiday.category}
                                      </span>
                                    </div>
                                    {holiday.is_recurring && (
                                      <span className="text-xs text-theme-blue bg-blue-50 px-1.5 py-0.5 rounded">
                                        Recurring
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Save buttons moved to individual tabs for better UX */}
          </div>
        </div>
      </div>
      
      {/* Save Progress Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                {saveProgress === 100 ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <RefreshCw className="h-6 w-6 text-theme-blue animate-spin" />
                )}
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {saveProgress === 100 ? 'Settings Saved!' : 'Saving Settings...'}
              </h3>
              
              <p className="text-sm text-gray-500 mb-4">
                {saveProgress === 100 
                  ? 'Your OpenRouter settings have been saved successfully.'
                  : 'Please wait while we save your OpenRouter configuration.'
                }
              </p>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div 
                  className="bg-theme-blue h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${saveProgress}%` }}
                ></div>
              </div>
              
              <div className="text-xs text-gray-400">
                {saveProgress}% Complete
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clean Usage Data Confirmation Modal */}
      {showCleanUsageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Clean All Usage Data?
              </h3>
              
              <p className="text-sm text-gray-500 mb-6">
                This action will permanently delete all usage data and analytics for all users. 
                This cannot be undone. Are you sure you want to proceed?
              </p>
              
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => setShowCleanUsageModal(false)}
                  disabled={cleaningUsageData}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={activeTab === 'openai' ? cleanOpenAIUsageData : cleanUsageData}
                  disabled={cleaningUsageData}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                >
                  {cleaningUsageData ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Cleaning...
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 mr-2" />
                      Yes, Clean Data
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compact Saving Modal */}
      {showSavingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-xs w-full">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 mb-3">
                <RefreshCw className="h-5 w-5 text-theme-blue animate-spin" />
              </div>
              
              <h3 className="text-base font-medium text-gray-900 mb-2">
                Saving Settings...
              </h3>
              
              <p className="text-sm text-gray-500">
                Please wait a moment
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 h-[80vh] flex flex-col">
            
            {/* Sticky Header */}
            <div 
              className="flex items-center justify-between px-6 py-4 rounded-t-lg"
              style={{ backgroundColor: 'var(--theme-primary)' }}
            >
              <h3 className="text-lg font-medium text-white flex items-center">
                <Edit3 className="w-5 h-5 mr-2" />
                Edit {getTemplateDisplayName(editingTemplate)}
              </h3>
              <button
                onClick={closeTemplateEditor}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {getTemplateDisplayName(editingTemplate)} Content
                  </label>
                  <button
                    onClick={openPlaceholderModal}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-md transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(35, 147, 180)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
                  >
                    <FileText className="w-4 h-4 mr-1.5" />
                    Available Placeholders
                  </button>
                </div>
                {editingTemplate === 'email' ? (
                  <textarea
                    ref={templateTextareaRef}
                    value={tempTemplateContent}
                    onChange={(e) => setTempTemplateContent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="12"
                    placeholder="Enter your template content..."
                  />
                ) : (
                  <textarea
                    ref={templateTextareaRef}
                    value={tempTemplateContent}
                    onChange={(e) => setTempTemplateContent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="6"
                    placeholder="Enter your template content..."
                  />
                )}
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={closeTemplateEditor}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={saveIndividualTemplate}
                disabled={templateSaving}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-opacity"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                {templateSaving ? (
                  <>
                    <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Save Modal Popup */}
      {saveModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="text-center">
              {saveModal.status === 'saving' && (
                <>
                  <RefreshCw className="w-8 h-8 mx-auto text-theme-blue animate-spin mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Saving Settings</h3>
                </>
              )}
              
              {saveModal.status === 'success' && (
                <>
                  <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Settings Saved!</h3>
                </>
              )}
              
              {saveModal.status === 'error' && (
                <>
                  <AlertCircle className="w-8 h-8 mx-auto text-red-600 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Save Failed</h3>
                </>
              )}
              
              <p className="text-sm text-gray-600 mb-4">{saveModal.message}</p>
              
              {saveModal.status === 'error' && (
                <button
                  onClick={() => setSaveModal({ show: false, platform: '', format: '', status: '', message: '' })}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm transition-colors"
                >
                  Close
                </button>
              )}
              
              {saveModal.status === 'success' && (
                <div className="text-xs text-gray-500">
                  Auto-closing in 2 seconds...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Image Format
                </h3>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete the <span className="font-semibold text-gray-900">"{deleteFormatData.formatName}"</span> format from <span className="font-semibold text-gray-900">{deleteFormatData.platform}</span>? This action cannot be undone.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteFormat}
                className="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Format
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Format Modal */}
      {showCreateFormatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Sticky Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-medium text-white">
                Create New Image Format for {createFormatPlatform}
              </h3>
              <button
                onClick={() => setShowCreateFormatModal(false)}
                className="text-white hover:text-gray-100 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {/* Format Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Format Key (unique identifier) *
                  </label>
                  <input
                    type="text"
                    value={newFormatKey}
                    onChange={(e) => setNewFormatKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    placeholder="e.g., custom_square_desktop"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use lowercase letters, numbers, and underscores only</p>
                </div>
                
                {/* Format Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Format Name (display name) *
                  </label>
                  <input
                    type="text"
                    value={newFormatName}
                    onChange={(e) => setNewFormatName(e.target.value)}
                    placeholder="e.g., Custom Square - 500×500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                
                {/* Dimensions */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Width (px) *
                    </label>
                    <input
                      type="number"
                      value={newFormatWidth}
                      onChange={(e) => setNewFormatWidth(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Height (px) *
                    </label>
                    <input
                      type="number"
                      value={newFormatHeight}
                      onChange={(e) => setNewFormatHeight(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>
                
                {/* Checkboxes */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="newFormatSignature"
                      checked={newFormatIncludeSignature}
                      onChange={(e) => setNewFormatIncludeSignature(e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <label htmlFor="newFormatSignature" className="text-sm text-gray-700">
                      Include AI Photo Signature
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="newFormatText"
                      checked={newFormatIncludeText}
                      onChange={(e) => setNewFormatIncludeText(e.target.checked)}
                      className="mr-2 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <label htmlFor="newFormatText" className="text-sm text-gray-700">
                      Include Text on Image
                    </label>
                  </div>
                </div>
                
                {/* AI Model Aspect Ratios */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">AI Model Aspect Ratios</h4>
                  
                  <div className="space-y-3">
                    {/* DALL-E 3 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        DALL-E 3 Size
                      </label>
                      <select
                        value={newFormatDalleAspect}
                        onChange={(e) => setNewFormatDalleAspect(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="1024x1024">1024×1024 (Square)</option>
                        <option value="1792x1024">1792×1024 (Landscape)</option>
                        <option value="1024x1792">1024×1792 (Portrait)</option>
                      </select>
                    </div>
                    
                    {/* GPT Image 1 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        GPT Image 1 Size
                      </label>
                      <select
                        value={newFormatGptImageAspect}
                        onChange={(e) => setNewFormatGptImageAspect(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="1024x1024">1024×1024 (Square)</option>
                        <option value="1536x1024">1536×1024 (Landscape)</option>
                        <option value="1024x1536">1024×1536 (Portrait)</option>
                      </select>
                    </div>
                    
                    {/* Nano Banana */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nano Banana (Gemini) Aspect Ratio
                      </label>
                      <select
                        value={newFormatGeminiAspect}
                        onChange={(e) => setNewFormatGeminiAspect(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="1:1">1:1 (Square)</option>
                        <option value="16:9">16:9 (Landscape)</option>
                        <option value="9:16">9:16 (Portrait)</option>
                        <option value="4:3">4:3 (Standard)</option>
                        <option value="3:4">3:4 (Portrait)</option>
                        <option value="2:3">2:3 (Portrait)</option>
                        <option value="3:2">3:2 (Landscape)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sticky Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowCreateFormatModal(false)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFormat}
                disabled={creatingFormat}
                className="px-4 py-2 text-sm font-medium rounded-md text-white transition-colors"
                style={{ 
                  backgroundColor: creatingFormat ? '#9CA3AF' : 'var(--theme-primary)',
                  cursor: creatingFormat ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!creatingFormat) {
                    e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!creatingFormat) {
                    e.target.style.backgroundColor = 'var(--theme-primary)';
                  }
                }}
              >
                {creatingFormat ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 inline animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Format'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Holiday Management Modals */}
      
      {/* Add/Edit Holiday Modal */}
      {(showAddHolidayModal || showEditHolidayModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-700" />
                  <h3 className="text-base font-semibold text-gray-900">
                    {showEditHolidayModal ? 'Edit Holiday' : 'Add Holiday'}
                  </h3>
                </div>
                <button
                  onClick={resetHolidayForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-5">
              <form onSubmit={handleHolidaySubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Holiday Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={holidayFormData.name}
                    onChange={(e) => setHolidayFormData({ ...holidayFormData, name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="e.g., New Year's Day, Christmas, Valentine's Day"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={holidayFormData.date}
                    onChange={(e) => handleHolidayDateChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="Select date"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    value={holidayFormData.category}
                    onChange={(e) => setHolidayFormData({ ...holidayFormData, category: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="general">General Holiday</option>
                    <option value="pet">Pet Days</option>
                    <option value="veterinary">Veterinary</option>
                    <option value="family">Family Occasion</option>
                  </select>
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={holidayFormData.is_recurring}
                      onChange={(e) => setHolidayFormData({ ...holidayFormData, is_recurring: e.target.checked })}
                      className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                    />
                    <span className="text-xs text-gray-700">Recurring Yearly</span>
                  </label>

                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={holidayFormData.is_enabled}
                      onChange={(e) => setHolidayFormData({ ...holidayFormData, is_enabled: e.target.checked })}
                      className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                    />
                    <span className="text-xs text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetHolidayForm}
                    className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={holidaysLoading}
                    className="flex-1 px-4 py-2 text-sm rounded-lg text-white font-medium transition-colors disabled:opacity-50"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    {holidaysLoading ? 'Saving...' : (showEditHolidayModal ? 'Update Holiday' : 'Add Holiday')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteHolidayModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Holiday</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete "{selectedHoliday?.name}"? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteHolidayModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteHoliday}
                  disabled={holidaysLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors disabled:opacity-50"
                >
                  {holidaysLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Dates Confirmation Modal */}
      {showRefreshHolidayModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <Calendar className="h-6 w-6 text-theme-blue" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Refresh Holiday Dates</h3>
              <p className="text-sm text-gray-600 mb-6">
                This will update all recurring holidays to their next occurrence based on the current date. Do you want to continue?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowRefreshHolidayModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={refreshHolidayDates}
                  disabled={holidaysLoading}
                  className="flex-1 px-4 py-2 rounded-lg text-white font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#10b981' }}
                >
                  {holidaysLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Placeholder Modal */}
      <PlaceholderModal
        isOpen={placeholderModalOpen}
        onClose={closePlaceholderModal}
        onInsertPlaceholder={insertPlaceholder}
      />
    </div>
  );
};

export default AISettings;